import type { Workflow } from '@compass/client/types/workflow';
import { workflowService } from '@services/api';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useStableFold } from '@/hooks/internal/useStableFold';

export interface OrgProgramPair {
  orgId: string;
  programId: string;
}

/**
 * Resolves `workflow_id → sla.minutes` for every workflow attached to the given
 * (organization, program) pairs. Supports cross-org fan-out. Workflows change
 * rarely → 10-minute staleTime.
 */
export const useWorkflowSlaMap = (pairs: OrgProgramPair[]) => {
  const uniquePairs = useMemo(() => {
    const seen = new Set<string>();
    const out: OrgProgramPair[] = [];
    for (const p of pairs) {
      const key = `${p.orgId}::${p.programId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
    return out;
  }, [pairs]);

  const queries = useQueries({
    queries: uniquePairs.map(({ orgId, programId }) => ({
      queryKey: ['compass', 'workforce', 'workflows', orgId, programId] as const,
      queryFn: () =>
        workflowService.getWorkflowsByProgram({
          organization_id: orgId,
          program_id: programId,
        }),
      enabled: !!orgId && !!programId,
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  const slaMap: Map<string, number> = useStableFold(
    queries,
    q => q.data?.workflows?.length ?? 0,
    qs => {
      const map = new Map<string, number>();
      for (const q of qs) {
        const workflows = q.data?.workflows ?? [];
        for (const wf of workflows) {
          if (wf.id && typeof wf.sla?.minutes === 'number') {
            map.set(wf.id, wf.sla.minutes);
          }
        }
      }
      return map;
    },
  );

  /**
   * Workflow record keyed by id — used by the container to look up
   * `workflow.ui.status[state].name` for the state label displayed on each
   * case card (via `getStatusLabel`).
   */
  const workflowsById: Map<string, Workflow> = useStableFold(
    queries,
    q => q.data?.workflows?.length ?? 0,
    qs => {
      const map = new Map<string, Workflow>();
      for (const q of qs) {
        const workflows = q.data?.workflows ?? [];
        for (const wf of workflows) {
          if (wf.id) map.set(wf.id, wf);
        }
      }
      return map;
    },
  );

  return {
    slaMap,
    workflowsById,
    isLoading: queries.some(q => q.isLoading),
  };
};
